/**
 * The dashboard's languages. The texts live per area in src/i18n/<lang>/<area>.ts with flat keys
 * (`area.name`); English is the source and TypeScript checks every other language against it
 * (npm run check:i18n also checks their {placeholders} and that no visible English is left).
 * The language in use comes from the server once signed in (the person's own, else the company's,
 * else the browser's) and from the browser before; see load.ts.
 */
import { create } from 'zustand'
import type { Plural, Translation } from './types'
import en from './en'
import nl from './nl'
import fr from './fr'
import de from './de'
import es from './es'

export const LANGUAGES = ['en', 'nl', 'fr', 'de', 'es'] as const
export type Lang = typeof LANGUAGES[number]
/** each language named in itself */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: 'English', nl: 'Nederlands', fr: 'Français', de: 'Deutsch', es: 'Español' }

const LOCALES: Record<Lang, string> = { en: 'en-GB', nl: 'nl-NL', fr: 'fr-FR', de: 'de-DE', es: 'es-ES' }
export const localeOf = (lang: Lang) => LOCALES[lang]

export type Key = keyof typeof en
const DICTIONARIES: Record<Lang, Translation<typeof en>> = { en, nl, fr, de, es }

/** a text exists for this key (for keys made from a code, such as a status) */
export const hasKey = (k: string): k is Key => k in en

export const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGUAGES as readonly string[]).includes(v)

/** The browser's first language that is one of the five (by its base language), else English */
export function browserLanguage(): Lang {
  const listed = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]
  return listed.map(l => l?.split('-')[0].toLowerCase()).find(isLang) ?? 'en'
}

export const useLanguage = create<{ lang: Lang; setLang: (lang: Lang) => void }>(set => ({
  lang: browserLanguage(),
  setLang: lang => set({ lang }),
}))
export const currentLanguage = () => useLanguage.getState().lang

/** A text in a language, with its {placeholders} filled in; a plural's form chosen by {count} */
export function translate(lang: Lang, key: Key, vars?: Record<string, string | number>): string {
  const value = (DICTIONARIES[lang][key] ?? en[key]) as string | Plural
  const text = typeof value === 'string' ? value
    : new Intl.PluralRules(LOCALES[lang]).select(Number(vars?.count ?? 0)) === 'one' ? value.one : value.other
  return vars ? text.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m)) : text
}

/** t(key, vars) in the language in use; the component follows a change of language */
export function useT(): (key: Key, vars?: Record<string, string | number>) => string {
  const lang = useLanguage(s => s.lang)
  return (key, vars) => translate(lang, key, vars)
}
